import { IFareCart, IUpdateItem } from "@interfaces";
import _ from "lodash";
import { useState, useCallback, useMemo } from "react";
import { CartItemComponent, CartItemHeader, TotalPriceComponent } from "@components";
import { breakpoint, groupCartItems } from "@utilities";
import styled from "styled-components";

interface CartModuleProps {
    items: IFareCart[];
    moveTo: (step: number) => void;
    handleDeletePopUp: (cartDeleteIds: string[]) => void;
    handleItemsSave: (items: IUpdateItem[]) => Promise<void>;
}

export const CartModule = ({ items, handleDeletePopUp, handleItemsSave }: CartModuleProps) => {
    const [cartItems, setCartItems] = useState<IFareCart[]>(() => items);
    const groupedCartItems = useMemo(() => groupCartItems(cartItems), [cartItems]);

    const handleCartItemQuantityUpdate = (updatedItem: IUpdateItem) => {
        setCartItems((prevItems) =>
            prevItems.map((item) =>
                item.id === updatedItem.cartItemId
                    ? {
                        ...item,
                        booking_quantity: updatedItem.quantity,
                        booking_data: updatedItem.bookingData,
                    }
                    : item
            ),
        );
    };
    const submitCartUpdates = useCallback(async () => {
        const updatePayload = cartItems.map((item) => ({
            cartItemId: item.id,
            quantity: item.booking_quantity ?? 0,
            bookingData: item.booking_data ?? [],
        }));
        await handleItemsSave(updatePayload);
    }, [cartItems, handleItemsSave]);


    return (
        <Container>
            <div className="cartWrapper">
                {_.map(groupedCartItems, (groupItems, groupId) => {
                    const first = groupItems[0];
                    return (
                        <Info key={groupId} className="infoWrapper">
                            <div className="info">
                                <CartItemHeader
                                    item={first}
                                    onDelete={() => handleDeletePopUp([String(first.id)])}
                                    showDeleteButton={true}
                                />
                                <Border />
                                <h2>{first.product?.json?.name ?? ""}</h2>
                                {groupItems.map((item) => (
                                    <CartItemComponent
                                        key={item.id}
                                        item={item}
                                        quantity={item.booking_quantity ?? 0}
                                        onQuantityChange={handleCartItemQuantityUpdate}
                                        handleDeletePopUp={(id) => handleDeletePopUp([String(id)])}
                                    />
                                ))}
                            </div>
                        </Info>
                    );
                })}
            </div>
            <div className="totalPriceContainer">
                <TotalPriceComponent
                    btnTitle="Checkout"
                    onBtnClick={submitCartUpdates}
                    page="cart"
                    items={cartItems}
                />
            </div>
        </Container>
    );
};

export const Info = styled.div`
    background-color: ${(props) => props.theme.colors.gray_6};
    padding-bottom: 0 !important;
    margin-bottom: 16px;
    .info {
        display: flex;
        flex-direction: column;
        background-color: #fff;
        padding: 16px;
        border-radius: 8px;
        .main {
            display: flex;
            gap: 12px;
        }
        .img {
            position: relative;
            cursor: pointer;
            height: 64px;
            width: 64px;
            display: flex;
            align-items: center;
            justify-content: center;
            img {
                height: 100%;
                width: 100%;
                object-fit: cover;
                border-radius: 8px;
            }
        }
        .info-container {
            display: flex;
            justify-content: space-between;
            width: 90%;
            ${breakpoint.breakOnlyMobile`
                width: auto;
            `}
        }
        .price-container {
            border: 1px solid #c3c9d0;
            border-radius: 8px;
            display: flex;
            justify-content: space-between;
            padding: 8px 20px;
            align-items: center;
            .price {
                display: flex;
                flex-direction: column;
                color: #6e7375;
                font-size: 12px;
                span {
                    color: #000;
                    font-size: 18px;
                }
            }
            .quantity {
                color: #6e7375;
                font-size: 12px;
                display: flex;
                align-items: center;
                gap: 10px;
                span {
                    font-size: 20px;
                    color: #1f2937;
                    width: 18px;
                    text-align: center;
                }
            }
        }
            .fareName{
                font-size: 18px;
                font-weight: 600;
            }
    }
    .name-container {
        display: flex;
        flex-direction: column;
        gap: 6px;
        .title {
            font-size: 14px;
            font-weight: 600;
            line-height: 18px;
            cursor: pointer;
            color: #131214;
            text-decoration: underline;
            text-underline-offset: 1px;
            @media (min-width: 375px) {
                width: 200px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            @media (min-width: 425px) {
                width: 250px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            ${breakpoint.breakLarge`
                font-size: 18px;
                font-weight: 700;
                line-height: 23px;
            `}
        }
        .sub-title {
            color: #6e7375;
            font-size: 12px;
            line-height: 14px;
            @media (min-width: 375px) {
                width: 200px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            @media (min-width: 425px) {
                width: 250px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }
        .location {
            display: flex;
            align-items: center;
            gap: 6px;
            color: #3c3c3c;
            font-size: 12px;
            font-weight: 500;
        }
    }
`;

export const Border = styled.div`
    border-top: 1px solid #e6e9eb;
    margin: 10px 0px 14px 0px;
`;

export const Container = styled.div`
    display: flex;
    font-family: Poppins;
    .cartWrapper {
        padding-right: 16px;
        ${breakpoint.breakOnlyMobile`
            padding-bottom: 100px;
        `}
    }
    ${breakpoint.breakTablet`
        & > .cartWrapper {
            width: ${(p) => p.theme.widths.wide_450};
        }
    `}
    ${breakpoint.breakIpadPro`
        & > .cartWrapper {
            width: ${(p) => p.theme.widths.wide_650};
        }
    `}
    ${breakpoint.breakLarge`
        & > .cartWrapper {
            width: ${(p) => p.theme.widths.wide_825};
        }
    `}
    ${breakpoint.breakOnlyMobile`
        display: block;
        padding: 16px 0px 0px 16px;
    `}
    .totalPriceContainer {
        ${breakpoint.breakLarge`
            width: 300px;
        `}
        ${breakpoint.breakOnlyMobile`
            position: fixed;
            bottom: 0;
            left: 0;
            background-color: white;
            z-index: 100;
            width: 100%;
        `}
    }
`;